import { useMemo } from "react"
import { gql } from "@apollo/client"
import { useTranslation } from "react-i18next"
import type {
  CountryIntelligenceRulesProbation,
  CountryIntelligenceRulesTermination,
  CountryIntelligenceRulesResignation,
  CountryIntelligenceRulesTimeOff,
} from "@oysterhr/schema"
import { CalendarDurationType } from "@oysterhr/schema"
import {
  concatPhrase,
  maxDuration,
  minDuration,
  timeWithUnit,
} from "../../hooks/useKBAdvisor/helpers"
import { getCountrySpecificTranslation } from "../../utils/DefaultAdvisorFallback"
import {
  ProbationRulesFragment,
  ResignationRulesFragment,
  TerminationRulesFragment,
  TimeOffRulesFragment,
} from "../../hooks/useKBIntegration/fragments"
import type { AdvisorKnowledgeBase } from "../../types"
import { useSubdivisionName } from "@oysterhr/feature-components"
import { probationText, introduction } from "./_helpers"
import { getMinimumVacationDays } from "~/shared/utils/annualLeave"

export type Module = AdvisorKnowledgeBase<{
  name: string
  flagUtf8: string
  isoAlpha2Code: string
  nationalHolidaysPerYear: number
  statutoryProbationPeriod: CountryIntelligenceRulesProbation[]
  recommendedProbationPeriod: CountryIntelligenceRulesProbation[]
  timeOffRules: CountryIntelligenceRulesTimeOff[]
  terminationRules: CountryIntelligenceRulesTermination[]
  resignationRules: CountryIntelligenceRulesResignation[]
}>

export const queryFragment: Module["queryFragment"] = gql`
  fragment kbCountryHighlights on CountryIntelligence {
    name
    flagUtf8
    isoAlpha2Code
    nationalHolidaysPerYear

    statutoryProbationPeriod: probationRules(oysterScore: STATUTORY) {
      ...ProbationRulesFragment
    }
    recommendedProbationPeriod: probationRules(oysterScore: RECOMMENDED) {
      ...ProbationRulesFragment
    }
    timeOffRules {
      ...TimeOffRulesFragment
    }
    resignationRules {
      ...ResignationRulesFragment
    }
    terminationRules {
      ...TerminationRulesFragment
    }
  }

  ${ProbationRulesFragment}
  ${TimeOffRulesFragment}
  ${ResignationRulesFragment}
  ${TerminationRulesFragment}
`

export const TranslationComponent: Module["TranslationComponent"] = ({ data, input }) => {
  const { t } = useTranslation("knowledge_base/HiringAdvisor")
  const subdivisionCode = input?.subdivisionCode
  const subdivisionName = useSubdivisionName(data.isoAlpha2Code || "", subdivisionCode || "")

  const phrases = useMemo(() => {
    const publicHolidays = data?.nationalHolidaysPerYear

    const maxPPDurationStatutory = maxDuration(data?.statutoryProbationPeriod?.map(p => p.duration))
    const maxPPRecommendedStatutory = maxDuration(
      data?.recommendedProbationPeriod?.map(p => p.duration),
    )
    const minTimeoffDays = getMinimumVacationDays(data?.timeOffRules)
    const minTimeOffDaysDuration = minTimeoffDays
      ? {
          value: minTimeoffDays,
          unit: CalendarDurationType.Days,
        }
      : null
    const resignationMaxNotice = maxDuration(data?.resignationRules?.map(r => r.notice))
    const terminationMinNotice = minDuration(data?.terminationRules?.map(r => r.notice))

    let content: (string | JSX.Element)[] = []
    if (maxPPDurationStatutory) {
      content.push(probationText(t, maxPPDurationStatutory, maxPPRecommendedStatutory))
    }
    content = concatPhrase(t, content, "countryHighlights.resignationMaxNotice", {
      resignationMaxNotice: timeWithUnit(t, resignationMaxNotice) || null,
    })
    content = concatPhrase(t, content, "countryHighlights.terminationMinNotice", {
      terminationMinNotice: timeWithUnit(t, terminationMinNotice) || null,
    })
    content = concatPhrase(t, content, "countryHighlights.minTimeOffDays", {
      minTimeOffDays: timeWithUnit(t, minTimeOffDaysDuration) || null,
    })
    content = concatPhrase(t, content, "countryHighlights.publicHolidays", { publicHolidays })

    return content
  }, [data, t])

  return (
    <div>
      {introduction(t, data.name, data.isoAlpha2Code, subdivisionName)}
      <ul className="list-inside list-disc py-2">
        {phrases?.map((phrase, index) => (
          <li key={index} className="pl-4">
            {phrase}
          </li>
        ))}
      </ul>
      {getCountrySpecificTranslation(t, data.isoAlpha2Code, false)}
    </div>
  )
}
